"use client";

import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Search, X, Filter, ChevronUp, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { getModuleIcon } from "@/utils/module-icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ChatConversation {
  id: string;
  title: string | null;
  moduleId: string;
  createdAt: Date | string | null;
}

interface ModuleInfo {
  id: string;
  name: string;
  locked?: boolean;
}

interface ChatHistoryBarProps {
  conversations: ChatConversation[];
  modules: ModuleInfo[];
  activeId: string | null;
  loadingId: string | null;
  moduleFilter: string;
  onModuleFilterChange: (value: string) => void;
  onConversationSelect: (conversation: ChatConversation) => void;
  onConversationDelete?: (conversationId: string, title: string) => void;
  hiddenModuleIds?: Set<string>;
}

const sweepSpring = {
  type: "spring" as const,
  stiffness: 300,
  damping: 30,
  mass: 0.6,
};

const formatTimeAgo = (
  date: Date | string | null,
  t: (key: string, opts?: Record<string, unknown>) => string
) => {
  if (!date) return "";
  const now = new Date();
  const chatDate = new Date(date);
  const diffInHours = Math.floor((now.getTime() - chatDate.getTime()) / (1000 * 60 * 60));

  if (diffInHours < 1) return t("chatHistory.now");
  if (diffInHours < 24) return t("chatHistory.hoursAgo", { hours: diffInHours });
  if (diffInHours < 48) return t("chatHistory.yesterday");
  return t("chatHistory.daysAgo", { days: Math.floor(diffInHours / 24) });
};

const ConversationItem = ({
  conversation,
  isActive,
  isLoading,
  modules,
  onDelete,
}: {
  conversation: ChatConversation;
  isActive: boolean;
  isLoading: boolean;
  modules: ModuleInfo[];
  onDelete?: (conversationId: string, title: string) => void;
}) => {
  const { t } = useTranslation();
  const moduleName =
    modules.find((m) => m.id === conversation.moduleId)?.name ?? conversation.moduleId;

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, x: 8, y: 10 },
        visible: { opacity: 1, x: 0, y: 0 },
      }}
      transition={sweepSpring}
      className={cn(
        "relative group rounded-lg cursor-pointer transition-all duration-75 py-2 px-2",
        isActive && "ring-1 ring-red-500/50 bg-red-900/20",
        isLoading && "opacity-80"
      )}
    >
      <span
        className={cn(
          "absolute inset-0 rounded-lg transition-colors duration-75 pointer-events-none",
          isActive
            ? "bg-gradient-to-r from-red-900/40 to-red-800/30"
            : "bg-transparent group-hover:bg-white/[0.04]"
        )}
      />
      <div className="relative flex items-center space-x-2.5">
        <span className="inline-flex items-center justify-center flex-shrink-0">
          {isLoading ? (
            <svg className="w-3 h-3 animate-spin text-red-400" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            getModuleIcon(conversation.moduleId, "w-3 h-3", "conversation")
          )}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-white text-xs font-medium truncate leading-tight">
            {conversation.title || t("chatHistory.newConversation")}
          </p>
          <div className="flex items-center space-x-1 mt-0.5">
            <span className="text-white/40 text-[10px] truncate">{moduleName}</span>
            <span className="text-white/30 text-[10px]">·</span>
            <span className="text-white/40 text-[10px] flex-shrink-0">
              {formatTimeAgo(conversation.createdAt, t)}
            </span>
          </div>
        </div>
        {onDelete && (
          <button
            className="opacity-0 group-hover:opacity-100 flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(conversation.id, conversation.title || t("chatHistory.newConversation"));
            }}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </motion.div>
  );
};

export function ChatHistoryBar({
  conversations,
  modules,
  activeId,
  loadingId,
  moduleFilter,
  onModuleFilterChange,
  onConversationSelect,
  onConversationDelete,
  hiddenModuleIds = new Set(),
}: ChatHistoryBarProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredConversations = useMemo(
    () =>
      searchQuery
        ? conversations.filter((c) =>
            (c.title || "").toLowerCase().includes(searchQuery.toLowerCase())
          )
        : conversations,
    [conversations, searchQuery]
  );

  const visibleModules = useMemo(
    () =>
      modules.filter(
        (m) =>
          !["idea-bank", "youtube-mode", "clone-yourself", "email-creator"].includes(m.id) &&
          !hiddenModuleIds.has(m.id)
      ),
    [modules, hiddenModuleIds]
  );

  return (
    <motion.div
      layout
      className={cn(
        "relative overflow-hidden border border-white/[0.08] bg-[#0c0e14] rounded-2xl flex flex-col mx-2",
        !isExpanded && "cursor-pointer hover:border-white/[0.12] transition-colors"
      )}
      animate={{
        height: isExpanded ? 340 : 56,
      }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 30,
        mass: 0.8,
      }}
      onClick={() => !isExpanded && setIsExpanded(true)}
    >
      {/* Collapsed / Header bar */}
      <div
        className={cn(
          "flex items-center justify-between px-3 h-14 shrink-0",
          isExpanded && "border-b border-white/[0.06]"
        )}
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-white/50">
            <MessageSquare className="w-4 h-4" />
          </div>
          <div>
            <p className="text-sm font-medium text-white leading-none">{t("chatHistory.title")}</p>
            <p className="text-[10px] text-white/40 mt-0.5">
              {t("chatHistory.conversationsCount", { count: conversations.length })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {!isExpanded && (
            <div className="flex items-center gap-1">
              <Filter className="w-3.5 h-3.5 text-white/30" />
              <ChevronUp className="w-3.5 h-3.5 text-white/30" />
            </div>
          )}

          {isExpanded && (
            <button
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-all active:scale-90"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
                setSearchQuery("");
              }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col flex-1 overflow-hidden"
          >
            {/* Search + filter row */}
            <div className="px-3 py-2 space-y-2 border-b border-white/[0.06]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-white/30 z-10" />
                <input
                  type="text"
                  placeholder={t("chatHistory.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-8 pl-7 pr-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-white/[0.15] transition-colors"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <Select value={moduleFilter} onValueChange={onModuleFilterChange}>
                  <SelectTrigger className="w-full bg-[#0a0c10] border-white/[0.08] text-white text-[11px] py-1.5 h-7">
                    <SelectValue placeholder={t("chatHistory.allModules")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
                    <SelectItem value="all" className="text-white hover:bg-white/[0.08] text-xs">
                      {t("chatHistory.allModules")}
                    </SelectItem>
                    {visibleModules.map((module) => (
                      <SelectItem
                        key={module.id}
                        value={module.id}
                        className="text-white hover:bg-white/[0.08] text-xs"
                      >
                        {module.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Conversation list */}
            <div className="flex-1 overflow-y-auto px-1.5 py-1.5">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: { staggerChildren: 0.025, delayChildren: 0.05 },
                  },
                  hidden: {},
                }}
                className="space-y-0.5"
              >
                {filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onConversationSelect(conversation);
                    }}
                  >
                    <ConversationItem
                      conversation={conversation}
                      isActive={activeId === conversation.id}
                      isLoading={loadingId === conversation.id}
                      modules={modules}
                      onDelete={onConversationDelete}
                    />
                  </div>
                ))}
                {filteredConversations.length === 0 && (
                  <div className="text-center py-6">
                    <MessageSquare className="w-6 h-6 text-white/20 mx-auto mb-2" />
                    <p className="text-white/30 text-xs">
                      {searchQuery ? t("chatHistory.emptySearch") : t("chatHistory.empty")}
                    </p>
                  </div>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default ChatHistoryBar;
